import * as React from "react"
import { Head, Link, router, usePage } from "@inertiajs/react"
import { ExternalLink, Languages } from "lucide-react"
import { AdminShell } from "@/components/AdminShell"
import { PageHeader } from "@/components/PageHeader"
import { Breadcrumbs } from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { EntryRenderer } from "@/components/pages-cms/EntryRenderer"
import { LanguageToggle } from "@/components/pages-cms/LanguageToggle"
import { PageMetadataEditor, type PageMetadataProps } from "@/components/pages-cms/PageMetadataEditor"
import { SectionShell } from "@/components/pages-cms/SectionShell"
import { SectionOrderHint, useSectionOrder } from "@/components/pages-cms/useSectionOrder"
import { Switch } from "@/components/ui/switch"
import type { Entry, Lang, LibraryPhoto } from "@/components/pages-cms/types"
import type { PageProps } from "@/types/inertia"

type Props = PageProps & {
  page: {
    key: string
    label: string
    path: string
    public_url: string
    brand_key: string
    brand_name: string
    // Whole-page on/off switch (static pages only — condition/therapy pages
    // use draft/published, and the two home pages are locked on).
    toggleable: boolean
    visible: boolean
    // Up/down arrows on the sections (PageSectionOrder) — only on pages whose
    // public component renders its bands in the stored order.
    reorderable: boolean
    custom_order: boolean
    // Both families of database-backed page carry a settings screen; only
    // one of the two is ever set.
    condition: { id: number; draft: boolean } | null
    therapy: { id: number; draft: boolean } | null
  }
  entries: Entry[]
  metadata: PageMetadataProps
  stale_count: number
  ai_enabled: boolean
  photo_library: LibraryPhoto[]
}

export default function PageEdit() {
  const { props, url } = usePage<Props>()
  const { page, entries, metadata, stale_count, ai_enabled, photo_library } = props
  const flash = props.flash

  // The active language lives in the URL (?lang=es) so it survives the full
  // page-prop refresh that follows a save or a translate redirect.
  const [lang, setLang] = React.useState<Lang>(url.includes("lang=es") ? "es" : "en")
  const [translatingAll, setTranslatingAll] = React.useState(false)

  // Condition and therapy pages are admin-created, so they have a settings
  // screen (address, labels, draft/published); static pages don't.
  const settingsHref = page.condition
    ? `/admin/website/conditions/${page.condition.id}/edit`
    : page.therapy
      ? `/admin/website/therapies/${page.therapy.id}/edit`
      : null

  function switchLang(next: Lang) {
    setLang(next)
    const params = new URLSearchParams(window.location.search)
    if (next === "es") params.set("lang", "es")
    else params.delete("lang")
    const query = params.toString()
    window.history.replaceState({}, "", window.location.pathname + (query ? `?${query}` : ""))
  }

  const { orderedEntries, moveHandlers, resetOrder } = useSectionOrder(page.key, entries, page.reorderable)

  function translateAll() {
    setTranslatingAll(true)
    router.post(
      "/admin/ai/translate",
      { page_key: page.key },
      { preserveScroll: true, onFinish: () => setTranslatingAll(false) },
    )
  }

  const description = `Edit the content of the ${page.label} page.`

  return (
    <AdminShell>
      <Head title={`Website Pages — ${page.label}`}>
        <meta name="description" content={description} />
        <meta property="og:title" content={`Website Pages — ${page.label}`} />
        <meta property="og:description" content={description} />
      </Head>

      <Breadcrumbs
        items={[
          { label: "Website Pages", href: "/admin/website" },
          { label: page.brand_name, href: `/admin/website?brand=${page.brand_key}` },
          { label: page.label },
        ]}
      />

      <PageHeader
        title={page.label}
        description={`Content shown on ${page.public_url}`}
        actions={
          <div className="flex flex-wrap items-center gap-3">
            {page.toggleable && (
              <Switch
                checked={page.visible}
                onChange={(visible) =>
                  router.patch(
                    `/admin/website/pages/${page.key}/visibility`,
                    { visible },
                    { preserveScroll: true }
                  )
                }
                label={page.visible ? "On the website" : "Off the website"}
              />
            )}
            {settingsHref && (
              <Button variant="secondary" asChild>
                <Link href={settingsHref}>Page settings</Link>
              </Button>
            )}
            <Button variant="secondary" asChild>
              <a href={page.public_url} target="_blank" rel="noopener">
                View page
                <ExternalLink className="ml-1.5 h-3.5 w-3.5" />
              </a>
            </Button>
          </div>
        }
      />

      {page.toggleable && !page.visible && (
        <p className="mt-4 text-sm text-ink-muted">
          This page is <strong>switched off</strong> — visitors get a “page not found”, it's out of
          the menus and Google's sitemap, and there's no preview while it's off. Everything below is
          kept and comes back the moment it's switched on. Links to it from inside other pages' copy
          will lead to the “page not found” screen until then.
        </p>
      )}

      {settingsHref && (page.condition?.draft || page.therapy?.draft) && (
        <p className="mt-4 text-sm text-ink-muted">
          This page is a <strong>draft</strong> — visitors get a “page not found”, and it isn't in
          Google's sitemap. You can preview it while logged in. Publish it from{" "}
          <Link href={settingsHref}>Page settings</Link>.
        </p>
      )}

      <div className="mt-4 flex flex-wrap items-center gap-3">
        <LanguageToggle lang={lang} onChange={switchLang} staleCount={stale_count} />
        {ai_enabled && stale_count > 0 && (
          <Button type="button" size="sm" variant="secondary" disabled={translatingAll} onClick={translateAll}>
            <Languages className="mr-1.5 h-3.5 w-3.5" />
            {translatingAll
              ? "Translating…"
              : `Translate ${stale_count} changed ${stale_count === 1 ? "item" : "items"} to Spanish`}
          </Button>
        )}
      </div>

      <p className="mt-4 text-sm text-ink-muted">
        Every box below saves on its own: change one and a tick and a cross appear under it — the
        tick keeps that change, the cross puts it back. Photos and the on/off switches save the
        moment you click them.
      </p>

      {page.reorderable && <SectionOrderHint customOrder={page.custom_order} onReset={resetOrder} />}

      {flash?.notice && <p className="mt-4 text-sm text-accent">{flash.notice}</p>}
      {flash?.alert && <p className="mt-4 text-sm text-danger-display">{flash.alert}</p>}

      <div className="max-w-3xl">
        <SectionShell id="metadata" title="Search & Social Metadata">
          <PageMetadataEditor pageKey={page.key} metadata={metadata} lang={lang} aiEnabled={ai_enabled} />
        </SectionShell>

        {orderedEntries.map((entry) => (
          <EntryRenderer
            key={entry.id}
            entry={entry}
            library={photo_library}
            lang={lang}
            aiEnabled={ai_enabled}
            move={moveHandlers(entry)}
          />
        ))}
      </div>
    </AdminShell>
  )
}
